/**
 * EN + AR copy for the Area X-Ray (AREA_SCORING_DESIGN).
 *
 * Same per-page copy pattern as the Price Brief and reports pages: a copy
 * object keyed by locale, read through a `t(key, locale, vars)` helper.
 * Arabic dimension labels come from the data (`name_ar`); this module only
 * carries UI chrome strings. Numerals stay Western in both locales.
 */

export type AreaXRayLocale = 'en' | 'ar'

type Localized = { en: string; ar: string }

function L(en: string, ar: string): Localized {
  return { en, ar }
}

export const AREA_XRAY_COPY = {
  pageTitle: L('Area X-Ray', 'أشعة المنطقة'),

  // Headline
  coverageScored: L('{{scored}} of {{total}} dimensions scored', '{{scored}} من أصل {{total}} أبعاد مقيّمة'),
  compositeLabel: L('Livability', 'قابلية العيش'),
  notScoredYet: L('Scoring has not run for this area yet', 'لم تُشغَّل التقييمات لهذه المنطقة بعد'),
  generatedSummaryFallback: L('No summary yet', 'لا ملخص بعد'),

  // The X-ray
  xrayEyebrow: L('The X-ray', 'الأشعة'),
  xrayHint: L('What each dimension contributes to the composite', 'ما يساهم به كل بُعد في النتيجة المركّبة'),
  compositeRow: L('Composite', 'النتيجة المركّبة'),
  scoreOutOf: L('{{score}} / 10', '{{score}} / 10'),
  unscored: L('unscored', 'غير مقيّم'),
  confidenceDotLabel: L('confidence', 'الثقة'),
  contributionReadout: L(
    '{{label}}, score {{score}}, contributes {{contribution}} of {{composite}}',
    '{{label}}، نقاط {{score}}، يساهم بـ {{contribution}} من {{composite}}',
  ),
  contributes: L('+{{value}}', '+{{value}}'),

  // What-if panel
  whatIfEyebrow: L('What-if', 'ماذا لو'),
  whatIfHint: L('The client cares most about…', 'ما يهم العميل أكثر…'),
  whatIfResult: L('With your weighting, {{from}} → {{to}}', 'مع أوزانك، {{from}} ← {{to}}'),
  resetWeights: L('Reset to platform weights', 'إعادة الأوزان إلى أوزان المنصة'),
  adjustedWatermark: L('Adjusted view — not the official score', 'عرض معدّل — ليس النتيجة الرسمية'),

  // WHY panel
  whyEyebrow: L('Why', 'لماذا'),
  noDimensionSelected: L('Select a dimension row to see its rationale.', 'اختر بُعدًا لعرض مبرراته.'),
  recalculated: L('Recalculated {{date}}', 'أُعيد التقييم في {{date}}'),
  basedOnSignals: L('Based on {{count}} signals', 'استنادًا إلى {{count}} إشارة'),
  signalReliability: L('reliability', 'الموثوقية'),
  signalDate: L('observed', 'رُصدت'),
  confidenceLabel: L('Confidence', 'الثقة'),

  // Around the area
  aroundEyebrow: L('Around the area', 'حول المنطقة'),
  aroundBasis: L('from our cached place surveys', 'من مسوح الأماكن المخزّنة لدينا'),
  placesEmpty: L('No place surveys cached for this area yet.', 'لا مسوح أماكن مخزّنة لهذه المنطقة بعد.'),
  category_restaurants: L('Restaurants', 'مطاعم'),
  category_cafes: L('Cafés', 'مقاهي'),
  category_groceries: L('Groceries & supermarkets', 'بقالات ومتاجر'),
  category_medical: L('Pharmacies & medical', 'صيدليات وطبية'),
  category_education: L('Schools & education', 'مدارس وتعليم'),
  category_fitness: L('Gyms & fitness', 'نوادٍ رياضية'),
  category_transport: L('Transport stops', 'مواصلات'),
  category_other: L('Other', 'أخرى'),
  medianRating: L('median {{value}}★', 'وسيط {{value}}★'),

  // Compare mode
  compareLabel: L('Compare', 'قارن'),
  compareWith: L('Compare with…', 'قارن مع…'),
  compositeDelta: L('Composite {{a}} vs {{b}} ({{delta}})', 'النتيجة {{a}} مقابل {{b}} ({{delta}})'),
  pickAreaPlaceholder: L('Pick an area', 'اختر منطقة'),

  // Collapsible sections
  propertiesSection: L('Properties in this area', 'عقارات في هذه المنطقة'),
  outlookSection: L('Investment outlook', 'النظرة الاستثمارية'),
  noProperties: L('No properties mapped to this area yet.', 'لا عقارات مرتبطة بهذه المنطقة بعد.'),
  noOutlook: L('No investment outlook recorded for this area yet.', 'لا نظرة استثمارية مسجّلة لهذه المنطقة بعد.'),

  // States
  loading: L('Loading the area…', 'جارٍ تحميل المنطقة…'),
  areaNotFound: L('Area not found, or scoring is not enabled for it.', 'المنطقة غير موجودة أو التقييم غير مفعّل لها.'),
  failedToLoad: L('The area could not be loaded.', 'تعذّر تحميل المنطقة.'),
  retry: L('Retry', 'إعادة المحاولة'),
} as const

export type AreaXRayCopyKey = keyof typeof AREA_XRAY_COPY

export function areaXRayT(
  key: AreaXRayCopyKey,
  locale: AreaXRayLocale,
  vars?: Record<string, string | number>,
): string {
  let text = AREA_XRAY_COPY[key][locale]
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      text = text.split(`{{${name}}}`).join(String(value))
    }
  }
  return text
}

/** Category chips, fixed order per spec §7. */
export const CATEGORY_ORDER = [
  'restaurants',
  'cafes',
  'groceries',
  'medical',
  'education',
  'fitness',
  'transport',
  'other',
] as const

export type CategoryKey = (typeof CATEGORY_ORDER)[number]

/**
 * Map a cached-survey query_category (free text from the Google fetcher) onto
 * the fixed chip categories. Unknown categories land in 'other' — never
 * dropped, never a blank chip.
 */
export function categoryFor(queryCategory: string | null | undefined): CategoryKey {
  const raw = String(queryCategory || '').toLowerCase()
  if (/restaurant|food|meal/.test(raw)) return 'restaurants'
  if (/cafe|coffee|bakery/.test(raw)) return 'cafes'
  if (/grocery|supermarket|market|store|shop/.test(raw)) return 'groceries'
  if (/pharmac|medical|clinic|hospital|doctor|health/.test(raw)) return 'medical'
  if (/school|education|universit|kindergarten/.test(raw)) return 'education'
  if (/gym|fitness|sport/.test(raw)) return 'fitness'
  if (/transit|transport|station|bus|metro|stop/.test(raw)) return 'transport'
  return 'other'
}
